import { useEffect, useState } from 'react';
import RenderIf from './RenderIf';
import { useEmbedded } from '@/store';

/**
 * Visibility component is used to conditionally render children based on the current environment.
 * It takes a 'visibleIn' prop which can be either 'web', 'mobile'. If the current environment matches the 'visibleIn' prop,
 * the children will be rendered. Otherwise, they will not be rendered.
 *
 * @param {React.ReactNode} children - The children to be rendered.
 * @param {string} [visibleIn = 'web'] - The environment in which the children should be visible. Can be 'web', 'mobile'.
 *
 * @returns {React.ReactNode} - The rendered children or null.
 *
 * @example
 * <Visibility visibleIn="web">
 *   <div>This will only be visible on web.</div>
 * </Visibility>
 *
 */

export default function Visibility({ children, visibleIn = 'web' }) {
  const [isClient, setIsClient] = useState(false);
  const { isEmbedded } = useEmbedded();

  const conditions = {
    web: !isEmbedded,
    embedded: isEmbedded,
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <>
      {isClient && (
        <RenderIf condition={conditions[visibleIn]}>{children}</RenderIf>
      )}
    </>
  );
}
